import clsx from 'clsx';
import { observer } from 'mobx-react';
import { type ReactEventHandler, type FC, useCallback } from 'react';
import { useStores } from '@root/src/stores';
import './index.scss';

interface BuyButtonMiniProps {
  productId: string | number;
  isReplace?: boolean;
  isDisabledIncrease?: boolean;
  isPreorder?: boolean;
  buttonSize?: 'small' | 'normal';
}

const BuyButtonMini: FC<BuyButtonMiniProps> = observer(
  ({ productId, isReplace, isDisabledIncrease, isPreorder, buttonSize }) => {
    const { cartStore } = useStores();
    const { productsAmount, updateCart, changeProductsAmount } = cartStore;

    const updateCartHandler = useCallback(
      (number: number): void => {
        changeProductsAmount(number, productId as number);
        updateCart(productId as number, productsAmount[productId]);
      },
      [changeProductsAmount, productId, productsAmount, updateCart]
    );

    const increaseProductInCart: ReactEventHandler<HTMLDivElement> =
      useCallback(
        (e): void => {
          e.preventDefault();
          e.stopPropagation();
          updateCartHandler(1);
        },
        [updateCartHandler]
      );

    const decreaseProductInCart: ReactEventHandler<HTMLDivElement> =
      useCallback(
        (e): void => {
          e.preventDefault();
          e.stopPropagation();
          updateCartHandler(-1);
        },
        [updateCartHandler]
      );

    const productQuantity = productsAmount[productId];
    const notZero = productQuantity !== undefined && productQuantity > 0;
    const isSmall = buttonSize === 'small';

    return (
      <div
        className={clsx(
          `buy-button-mini`,
          notZero && 'buy-button-mini--fw',
          isPreorder && 'buy-button-mini--preorder',
          isSmall && 'buy-button-mini--small'
        )}
      >
        {notZero && (
          <>
            <div
              className={clsx(
                'buy-button-mini__minus',
                isSmall && 'buy-button-mini__minus--small'
              )}
              onClick={decreaseProductInCart}
            />
            <div className='buy-button-mini__count'>{productQuantity}</div>
          </>
        )}
        {isReplace && !notZero ? (
          <>
            {isReplace ? (
              <div
                className='buy-button-mini__plus-isReplace'
                onClick={increaseProductInCart}
              >
                Заменить
              </div>
            ) : isPreorder ? (
              <div
                onClick={increaseProductInCart}
                className='buy-button-mini__plus-isReplace buy-button-mini__plus-isReplace--preorder'
              >
                Заказ на {isPreorder}
              </div>
            ) : (
              <div
                className={'buy-button-mini__plus-isReplace'}
                onClick={increaseProductInCart}
              >
                Купить
              </div>
            )}
          </>
        ) : (
          <div
            className={clsx(
              'buy-button-mini__plus',
              isDisabledIncrease && 'disabled',
              isSmall && 'buy-button-mini__plus--small'
            )}
            onClick={!isDisabledIncrease ? increaseProductInCart : undefined}
          />
        )}
      </div>
    );
  }
);

export default BuyButtonMini;
